// WOS-BUNDLE in the in-memory twin: company tags (Settings → Tags renames, recolours, merges and
// deletes them; managers only), tags on tasks (by id or by name, made on demand: Work OS
// `tagTask`), the bulk `tag` action, task links (relates to, duplicates / duplicated by; blocks /
// blocked by are the Work twin's hard dependencies), each person's favorites (ordered, only what
// they may still open) and recents (ten, move to front). Same shapes and rules as the contracts
// (`contracts/api/work-bundle.ts`); `fake-work-bundle-contract.test.ts` holds it to them. A task's
// tags live on the Work twin's task row (`tag_ids`), so every card carries them.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { cleanTagName, type TagColor, tagColorFor, tagNamed } from "../features/work/tag-logic";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  peekJson,
  problem,
  validation,
} from "./fake";
import { uid } from "./fake-data";
import type { WorkBridge } from "./fake-work";

type Tag = OperationOutput<"tags.create">;
type FavoriteRow = OperationOutput<"favorites.add">;
type FavoriteView = OperationOutput<"favorites.list">["data"][number];
type RecentView = OperationOutput<"recents.list">["data"][number];
type LinkView = OperationOutput<"tasks.links">["data"][number];
type Relation = LinkView["relation"];
type FavKind = FavoriteRow["kind"];
type RecentKind = RecentView["kind"];

const RELATIONS: readonly Relation[] = [
  "relates_to",
  "duplicates",
  "duplicated_by",
  "blocks",
  "blocked_by",
];
const FAV_KINDS: readonly FavKind[] = ["space", "board", "page", "task", "channel"];
const RECENT_KINDS: readonly RecentKind[] = ["space", "board", "page", "task"];
const RECENTS_CAP = 10;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Seed --------------------------------------------------------------------------------------------

/** The demo tags every company starts with (ids stable per company, so cards and tags agree). */
const SEED_TAGS: readonly { key: string; name: string; color: TagColor }[] = [
  { key: "backend", name: "Backend", color: "sky" },
  { key: "client", name: "Client-facing", color: "rose" },
  { key: "legal", name: "Needs legal", color: "amber" },
  { key: "q4", name: "Q4 push", color: "violet" },
  { key: "quick", name: "Quick win", color: "emerald" },
  { key: "sales", name: "Sales", color: "orange" },
];

/** Which seeded tasks (by title) carry which tags. */
const SEED_TASK_TAGS: Record<string, readonly string[]> = {
  "Screen 12 backend applicants from LinkedIn": ["backend", "q4"],
  "Panel interview: Priya N. (Backend engineer)": ["backend"],
  "Offer letter for Tomás R.": ["legal", "q4"],
  "Source 30 account executives in Berlin": ["sales", "q4"],
  "Personalised outreach to the top 15 AEs": ["sales"],
  "Friday report for the Acme leadership": ["client"],
  "Renew the job board subscription": ["quick"],
  "Sign the Northwind statement of work": ["client", "legal"],
  "Kick-off email to the hiring managers": ["client", "quick"],
  "Clean up candidate tags in the ATS": ["quick"],
};

/** Seeded `relates_to` / `duplicates` links, by task titles. */
const SEED_LINKS: readonly { from: string; to: string; kind: "relates_to" | "duplicates" }[] = [
  {
    from: "Reference checks for Priya N.",
    to: "Offer letter for Tomás R.",
    kind: "relates_to",
  },
  {
    from: "Interview kit for account executives",
    to: "Personalised outreach to the top 15 AEs",
    kind: "relates_to",
  },
  {
    from: "Screen office manager referrals",
    to: "Screen 12 backend applicants from LinkedIn",
    kind: "relates_to",
  },
  {
    from: "Follow up with last week's candidates",
    to: "Screen 12 backend applicants from LinkedIn",
    kind: "duplicates",
  },
];

const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 4096;
const seedTagId = (companyId: string, i: number) => uid(0x7a10, companyNo(companyId) * 16 + i);

/** The seeded tags of a task (fake-work-data.ts `seedWork` stamps them on the row). */
export function seedTaskTagIds(companyId: string, title: string): string[] {
  return (SEED_TASK_TAGS[title] ?? []).flatMap((k) => {
    const i = SEED_TAGS.findIndex((t) => t.key === k);
    return i < 0 ? [] : [seedTagId(companyId, i)];
  });
}

// The twin ----------------------------------------------------------------------------------------

type StoredLink = {
  task_id: string;
  linked_task_id: string;
  kind: "relates_to" | "duplicates";
  created_at: string;
};
type Recent = { kind: RecentKind; object_id: string; viewed_at: string };
type State = {
  tags: Tag[];
  links: StoredLink[];
  /** membership id → its stars. */
  favorites: Map<string, FavoriteRow[]>;
  /** membership id → its recents, newest first. */
  recents: Map<string, Recent[]>;
};

export type WorkBundleDeps = {
  now: () => Date;
  work: () => WorkBridge | null;
  /** The Work twin's own handlers (dependencies for blocks / blocked by, bulk for other kinds). */
  workHandlers: Partial<Record<RouteId, Handler>>;
  /** A page and a channel as the caller may open them (favorites of docs and channels). */
  pageGet?: Handler | undefined;
  channelGet?: Handler | undefined;
  /** Hands out a reader of each person's starred boards (the Calendar's tasks overlay). */
  exposeStars?: (starredBoards: (p: Principal, membershipId: string) => string[]) => void;
};

export function createWorkBundleFake(deps: WorkBundleDeps): Partial<Record<RouteId, Handler>> {
  const states = new Map<string, State>();
  let seq = 0x400;
  const stamp = () => deps.now().toISOString();
  const nextId = () => uid(0x7a20, ++seq);

  function stateOf(companyId: string): State {
    let s = states.get(companyId);
    if (s) return s;
    const at = new Date(deps.now().getTime() - 14 * 86_400_000).toISOString();
    s = {
      tags: SEED_TAGS.map((t, i) => ({
        company_id: companyId,
        id: seedTagId(companyId, i),
        name: t.name,
        color: t.color,
        actor_kind: "system",
        actor_membership_id: null,
        created_at: at,
        updated_at: at,
      })),
      links: [],
      favorites: new Map(),
      recents: new Map(),
    };
    states.set(companyId, s);
    const tasks = deps.work()?.tasksOf(companyId) ?? [];
    const byTitle = (title: string) => tasks.find((t) => t.title === title);
    for (const l of SEED_LINKS) {
      const a = byTitle(l.from);
      const b = byTitle(l.to);
      if (a && b) s.links.push(stored(a.id, b.id, l.kind, at));
    }
    return s;
  }

  /** Symmetric links are stored once, sorted (Work OS `linkTasks`); `duplicates` is directed. */
  function stored(a: string, b: string, kind: StoredLink["kind"], at: string): StoredLink {
    const [x, y] = kind === "relates_to" && b < a ? [b, a] : [a, b];
    return { task_id: x, linked_task_id: y, kind, created_at: at };
  }

  // Principals --------------------------------------------------------------------------------

  /** Client viewers hold no relation on the spaces: they see no work (05). */
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  /** Favorites and recents are a person's own: the caller's person membership here. */
  const personOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person")
      ?.membership_id ?? null;
  const actorOf = (p: Principal) => {
    const m = p.me.memberships.find((x) => x.company_id === p.company.id)?.membership_id ?? null;
    return m
      ? { actor_kind: "person" as const, actor_membership_id: m }
      : { actor_kind: "agency_user" as const, actor_membership_id: null };
  };
  const forbidden = (detail: string) => problem(403, "forbidden", "Forbidden", detail);
  const noContent = () => new Response(null, { status: 204 });

  const taskOf = (p: Principal, id: string | undefined) =>
    id ? (deps.work()?.taskOf(p.company.id, id) ?? null) : null;
  const boardOf = (p: Principal, id: string) => deps.work()?.boardOf(p.company.id, id) ?? null;
  const spaceOf = (p: Principal, id: string | null) =>
    id
      ? (deps
          .work()
          ?.spacesOf(p.company.id)
          ?.find((s) => s.id === id) ?? null)
      : null;

  /** Calls another twin's read as the caller; its body when it answered 2xx. */
  function read<T>(h: Handler | undefined, ctx: Ctx, params: Record<string, string>): T | null {
    if (!h) return null;
    const res = h({ ...ctx, params: { co: ctx.params.co ?? "", ...params }, body: undefined });
    return peekJson<T>(res) ?? null;
  }

  /** What a favorite or recent points at, as the caller may open it (null: gone or hidden). */
  function resolve(
    ctx: Ctx,
    p: Principal,
    kind: FavKind,
    id: string,
  ): { title: string; space_id: string | null; board_id: string | null; path: string[] } | null {
    if (kind === "space") {
      if (!reader(p)) return null;
      const s = spaceOf(p, id);
      return s && !s.archived_at
        ? { title: s.name, space_id: s.id, board_id: null, path: [] }
        : null;
    }
    if (kind === "board") {
      if (!reader(p)) return null;
      const b = boardOf(p, id);
      if (!b) return null;
      const s = spaceOf(p, b.space_id);
      return { title: b.name, space_id: b.space_id, board_id: null, path: s ? [s.name] : [] };
    }
    if (kind === "task") {
      if (!reader(p)) return null;
      const t = taskOf(p, id);
      if (!t) return null;
      const b = boardOf(p, t.board_id);
      const s = b ? spaceOf(p, b.space_id) : null;
      return {
        title: t.title,
        space_id: b?.space_id ?? null,
        board_id: t.board_id,
        path: [...(s ? [s.name] : []), ...(b ? [b.name] : [])],
      };
    }
    if (kind === "page") {
      const pg = read<{ title: string; space_id: string | null; archived_at: string | null }>(
        deps.pageGet,
        ctx,
        { id },
      );
      if (!pg || pg.archived_at) return null;
      const s = spaceOf(p, pg.space_id);
      return {
        title: pg.title || "Untitled",
        space_id: pg.space_id,
        board_id: null,
        path: s ? [s.name] : ["Docs library"],
      };
    }
    const ch = read<{ name: string; space_id: string | null; archived_at: string | null }>(
      deps.channelGet,
      ctx,
      { id },
    );
    return ch && !ch.archived_at
      ? { title: ch.name, space_id: ch.space_id, board_id: null, path: [] }
      : null;
  }

  // Tags --------------------------------------------------------------------------------------

  const liveTasks = (p: Principal) => deps.work()?.tasksOf(p.company.id) ?? [];
  const tagView = (p: Principal, t: Tag) => ({
    ...t,
    task_count: liveTasks(p).filter((x) => x.tag_ids.includes(t.id)).length,
  });
  const byName = (a: Tag, b: Tag) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

  function makeTag(p: Principal, name: string, color: TagColor | undefined): Tag {
    const at = stamp();
    const tag: Tag = {
      company_id: p.company.id,
      id: nextId(),
      name,
      color: color ?? tagColorFor(name),
      ...actorOf(p),
      created_at: at,
      updated_at: at,
    };
    stateOf(p.company.id).tags.push(tag);
    return tag;
  }

  const isColor = (v: unknown): v is TagColor =>
    typeof v === "string" &&
    ["zinc", "violet", "sky", "emerald", "amber", "rose", "indigo", "teal", "orange"].includes(v);

  // Links -------------------------------------------------------------------------------------

  function linksOf(p: Principal, taskId: string): LinkView[] {
    const s = stateOf(p.company.id);
    const t = taskOf(p, taskId);
    if (!t) return [];
    const view = (relation: Relation, otherId: string, created_at: string): LinkView[] => {
      const o = taskOf(p, otherId);
      return o
        ? [
            {
              relation,
              created_at,
              task: {
                id: o.id,
                number: o.number,
                title: o.title,
                status: o.status,
                board_id: o.board_id,
                completed_at: o.completed_at,
              },
            },
          ]
        : [];
    };
    const out: LinkView[] = [];
    for (const l of s.links) {
      if (l.kind === "relates_to") {
        if (l.task_id === taskId) out.push(...view("relates_to", l.linked_task_id, l.created_at));
        else if (l.linked_task_id === taskId)
          out.push(...view("relates_to", l.task_id, l.created_at));
      } else if (l.task_id === taskId)
        out.push(...view("duplicates", l.linked_task_id, l.created_at));
      else if (l.linked_task_id === taskId)
        out.push(...view("duplicated_by", l.task_id, l.created_at));
    }
    // Hard dependencies read as blocks / blocked by (soft ones stay the drawer's "Related").
    for (const d of t.dependencies)
      if (d.kind === "hard") out.push(...view("blocked_by", d.depends_on_task_id, d.created_at));
    for (const o of liveTasks(p))
      for (const d of o.dependencies)
        if (d.kind === "hard" && d.depends_on_task_id === taskId)
          out.push(...view("blocks", o.id, d.created_at));
    return out.sort(
      (a, b) =>
        RELATIONS.indexOf(a.relation) - RELATIONS.indexOf(b.relation) ||
        a.task.number - b.task.number,
    );
  }

  /** The dependency a blocks / blocked by link is: `waiter` depends (hard) on `on`. */
  const depEnds = (relation: "blocks" | "blocked_by", task: string, other: string) =>
    relation === "blocks" ? { waiter: other, on: task } : { waiter: task, on: other };

  // Favorites and recents ---------------------------------------------------------------------

  function starsOf(p: Principal, m: string): FavoriteRow[] {
    const s = stateOf(p.company.id);
    let list = s.favorites.get(m);
    if (!list) {
      list = seedStars(p, m);
      s.favorites.set(m, list);
    }
    return list;
  }
  deps.exposeStars?.((p, m) =>
    starsOf(p, m)
      .filter((f) => f.kind === "board")
      .map((f) => f.object_id),
  );

  /** A person's first look at Favorites: the company's first board and space are starred. */
  function seedStars(p: Principal, m: string): FavoriteRow[] {
    const work = deps.work();
    const spaces = work?.spacesOf(p.company.id) ?? [];
    const firstSpace = [...spaces].sort((a, b) => a.position - b.position)[0];
    const board = firstSpace ? work?.sidebarOf(p.company.id, firstSpace.id)?.boards[0] : undefined;
    const at = new Date(deps.now().getTime() - 7 * 86_400_000).toISOString();
    const rows: FavoriteRow[] = [];
    const add = (kind: FavKind, id: string) =>
      rows.push({
        company_id: p.company.id,
        membership_id: m,
        kind,
        object_id: id,
        position: rows.length,
        created_at: at,
        updated_at: at,
      });
    if (board) add("board", board.id);
    if (firstSpace) add("space", firstSpace.id);
    return rows;
  }

  function favoriteViews(ctx: Ctx, p: Principal, m: string): FavoriteView[] {
    return [...starsOf(p, m)]
      .sort((a, b) => a.position - b.position)
      .flatMap((f) => {
        const r = resolve(ctx, p, f.kind, f.object_id);
        return r
          ? [
              {
                kind: f.kind,
                id: f.object_id,
                title: r.title,
                position: f.position,
                space_id: r.space_id,
                board_id: r.board_id,
              },
            ]
          : [];
      });
  }

  const renumber = (list: FavoriteRow[]) =>
    list.forEach((f, i) => {
      f.position = i;
    });

  // Routes ------------------------------------------------------------------------------------

  return {
    "tags.list": ({ principal }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json({ data: [] });
      const s = stateOf(principal.company.id);
      return json({ data: [...s.tags].sort(byName).map((t) => tagView(principal, t)) });
    },
    "tags.create": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as { name?: unknown; color?: unknown };
      const name = typeof b.name === "string" ? cleanTagName(b.name) : null;
      if (!name) return validation("name: Tag name cannot be empty");
      if (b.color !== undefined && !isColor(b.color)) return validation("color: not a tag colour");
      if (tagNamed(stateOf(principal.company.id).tags, name))
        return problem(409, "conflict", "Conflict", `A tag named "${name}" exists.`);
      return json(makeTag(principal, name, b.color as TagColor | undefined), 201);
    },
    "tags.update": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      if (!principal.manager) return forbidden("Only a manager renames or recolours tags.");
      const s = stateOf(principal.company.id);
      const tag = s.tags.find((t) => t.id === params.id);
      if (!tag) return notFound();
      const b = (body ?? {}) as { name?: unknown; color?: unknown };
      if (b.color !== undefined && !isColor(b.color)) return validation("color: not a tag colour");
      let name = tag.name;
      if (b.name !== undefined) {
        const n = typeof b.name === "string" ? cleanTagName(b.name) : null;
        if (!n) return validation("name: Tag name cannot be empty");
        const other = tagNamed(s.tags, n);
        if (other && other.id !== tag.id)
          return problem(409, "conflict", "Conflict", `A tag named "${n}" exists.`);
        name = n;
      }
      const color = (b.color as TagColor | undefined) ?? tag.color;
      if (name !== tag.name || color !== tag.color) {
        tag.name = name;
        tag.color = color;
        tag.updated_at = stamp();
      }
      return json(tag);
    },
    "tags.merge": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      if (!principal.manager) return forbidden("Only a manager merges tags.");
      const s = stateOf(principal.company.id);
      const from = s.tags.find((t) => t.id === params.id);
      if (!from) return notFound();
      const intoId = (body as { into_tag_id?: unknown } | undefined)?.into_tag_id;
      const into = s.tags.find((t) => t.id === intoId);
      if (!into) return validation("into_tag_id: no such tag");
      if (into.id === from.id) return validation("into_tag_id: a tag cannot merge into itself");
      for (const t of liveTasks(principal)) {
        if (!t.tag_ids.includes(from.id)) continue;
        t.tag_ids = [...new Set(t.tag_ids.map((x) => (x === from.id ? into.id : x)))];
      }
      s.tags = s.tags.filter((t) => t.id !== from.id);
      return json(into);
    },
    "tags.delete": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      if (!principal.manager) return forbidden("Only a manager deletes tags.");
      const s = stateOf(principal.company.id);
      if (!s.tags.some((t) => t.id === params.id)) return notFound();
      s.tags = s.tags.filter((t) => t.id !== params.id);
      for (const t of liveTasks(principal)) t.tag_ids = t.tag_ids.filter((x) => x !== params.id);
      return noContent();
    },
    "tasks.addTag": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskOf(principal, params.id);
      if (!t) return notFound();
      const s = stateOf(principal.company.id);
      const b = (body ?? {}) as { tag_id?: unknown; name?: unknown; color?: unknown };
      if ((b.tag_id === undefined) === (b.name === undefined))
        return validation("give exactly one of tag_id and name");
      let tag: Tag | null;
      if (b.tag_id !== undefined) {
        tag = s.tags.find((x) => x.id === b.tag_id) ?? null;
        if (!tag) return validation("tag_id: no such tag");
      } else {
        const name = typeof b.name === "string" ? cleanTagName(b.name) : null;
        if (!name) return validation("name: Tag name cannot be empty");
        if (b.color !== undefined && !isColor(b.color))
          return validation("color: not a tag colour");
        tag = tagNamed(s.tags, name) ?? makeTag(principal, name, b.color as TagColor | undefined);
      }
      if (!t.tag_ids.includes(tag.id)) t.tag_ids = [...t.tag_ids, tag.id];
      return json(tag, 201);
    },
    "tasks.removeTag": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskOf(principal, params.id);
      if (!t) return notFound();
      t.tag_ids = t.tag_ids.filter((x) => x !== params.item_id);
      return noContent();
    },
    // The bulk `tag` action here; every other kind is the Work twin's.
    "tasks.bulk": (ctx) => {
      const { principal, body } = ctx;
      const b = (body ?? {}) as {
        task_ids?: unknown;
        action?: { kind?: unknown; tag_id?: unknown; op?: unknown };
      };
      if (b.action?.kind !== "tag") return deps.workHandlers["tasks.bulk"]?.(ctx) ?? notFound();
      if (!reader(principal)) return notFound();
      if (
        !Array.isArray(b.task_ids) ||
        !b.task_ids.length ||
        b.task_ids.length > 200 ||
        !b.task_ids.every((x) => typeof x === "string" && UUID.test(x))
      )
        return validation("task_ids: 1–200 task ids");
      const tag = stateOf(principal.company.id).tags.find((x) => x.id === b.action?.tag_id);
      if (!tag) return validation("action.tag_id: no such tag");
      if (b.action.op !== "add" && b.action.op !== "remove")
        return validation("action.op: add or remove");
      const add = b.action.op === "add";
      const results = (b.task_ids as string[]).map((id) => {
        const t = taskOf(principal, id);
        if (!t) return { task_id: id, ok: false, code: "not_found" };
        t.tag_ids = add
          ? [...new Set([...t.tag_ids, tag.id])]
          : t.tag_ids.filter((x) => x !== tag.id);
        return { task_id: id, ok: true, code: null };
      });
      return json({ results });
    },

    "tasks.links": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      if (!taskOf(principal, params.id)) return notFound();
      return json({ data: linksOf(principal, params.id ?? "") });
    },
    "tasks.addLink": (ctx) => {
      const { principal, params, body } = ctx;
      if (!reader(principal)) return notFound();
      const t = taskOf(principal, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as { relation?: unknown; task_id?: unknown };
      const relation = b.relation as Relation;
      if (!RELATIONS.includes(relation)) return validation("relation: not a link relation");
      if (typeof b.task_id !== "string" || !UUID.test(b.task_id))
        return validation("task_id: a task id");
      if (b.task_id === t.id) return validation("task_id: A task cannot link to itself");
      const other = taskOf(principal, b.task_id);
      if (!other) return notFound();
      const found = () =>
        linksOf(principal, t.id).find((l) => l.relation === relation && l.task.id === other.id);
      const existing = found();
      // Linking twice changes nothing (Work OS `onConflictDoNothing`).
      if (existing) return json(existing, 201);
      if (relation === "blocks" || relation === "blocked_by") {
        const { waiter, on } = depEnds(relation, t.id, other.id);
        const res = deps.workHandlers["tasks.addDependency"]?.({
          ...ctx,
          params: { co: params.co ?? "", id: waiter },
          body: { depends_on_task_id: on, kind: "hard" },
        });
        if (!res) return notFound();
        if (!res.ok) return res;
      } else {
        const s = stateOf(principal.company.id);
        const kind = relation === "relates_to" ? "relates_to" : "duplicates";
        const [a, z] = relation === "duplicated_by" ? [other.id, t.id] : [t.id, other.id];
        s.links.push(stored(a, z, kind, stamp()));
      }
      const made = found();
      return made ? json(made, 201) : notFound();
    },
    "tasks.removeLink": (ctx) => {
      const { principal, params, url } = ctx;
      if (!reader(principal)) return notFound();
      const t = taskOf(principal, params.id);
      if (!t) return notFound();
      const relation = url.searchParams.get("relation") as Relation | null;
      if (!relation || !RELATIONS.includes(relation))
        return validation("relation: not a link relation");
      const otherId = params.item_id ?? "";
      if (relation === "blocks" || relation === "blocked_by") {
        const { waiter, on } = depEnds(relation, t.id, otherId);
        const w = taskOf(principal, waiter);
        if (w?.dependencies.some((d) => d.depends_on_task_id === on && d.kind === "hard"))
          deps.workHandlers["tasks.removeDependency"]?.({
            ...ctx,
            params: { co: params.co ?? "", id: waiter, item_id: on },
          });
        return noContent();
      }
      const s = stateOf(principal.company.id);
      s.links = s.links.filter((l) => {
        if (relation === "relates_to")
          return !(
            l.kind === "relates_to" &&
            ((l.task_id === t.id && l.linked_task_id === otherId) ||
              (l.task_id === otherId && l.linked_task_id === t.id))
          );
        const [a, z] = relation === "duplicates" ? [t.id, otherId] : [otherId, t.id];
        return !(l.kind === "duplicates" && l.task_id === a && l.linked_task_id === z);
      });
      return noContent();
    },

    "favorites.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      return json({ data: m ? favoriteViews(ctx, p, m) : [] });
    },
    "favorites.add": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      if (!m) return forbidden("Favorites are a person's own.");
      const b = (ctx.body ?? {}) as { kind?: unknown; object_id?: unknown };
      const kind = b.kind as FavKind;
      if (!FAV_KINDS.includes(kind)) return validation("kind: space, board, page, task or channel");
      if (typeof b.object_id !== "string" || !UUID.test(b.object_id))
        return validation("object_id: an id");
      if (!resolve(ctx, p, kind, b.object_id)) return notFound();
      const list = starsOf(p, m);
      const had = list.find((f) => f.kind === kind && f.object_id === b.object_id);
      if (had) return json(had, 201);
      const at = stamp();
      const row: FavoriteRow = {
        company_id: p.company.id,
        membership_id: m,
        kind,
        object_id: b.object_id,
        position: list.reduce((n, f) => Math.max(n, f.position + 1), 0),
        created_at: at,
        updated_at: at,
      };
      list.push(row);
      return json(row, 201);
    },
    "favorites.remove": ({ principal, params }) => {
      if (!principal) return notFound();
      const m = personOf(principal);
      if (!m) return forbidden("Favorites are a person's own.");
      const s = stateOf(principal.company.id);
      const list = starsOf(principal, m).filter(
        (f) => !(f.kind === params.kind && f.object_id === params.id),
      );
      renumber(list);
      s.favorites.set(m, list);
      return noContent();
    },
    "favorites.reorder": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      if (!m) return forbidden("Favorites are a person's own.");
      const items = (ctx.body as { items?: unknown } | undefined)?.items;
      if (!Array.isArray(items) || items.length > 200) return validation("items: up to 200 stars");
      const list = [...starsOf(p, m)].sort((a, b) => a.position - b.position);
      const named: FavoriteRow[] = [];
      for (const it of items as { kind?: unknown; object_id?: unknown }[]) {
        const f = list.find((x) => x.kind === it.kind && x.object_id === it.object_id);
        if (f && !named.includes(f)) named.push(f);
      }
      const next = [...named, ...list.filter((f) => !named.includes(f))];
      const at = stamp();
      next.forEach((f, i) => {
        if (f.position !== i) f.updated_at = at;
        f.position = i;
      });
      stateOf(p.company.id).favorites.set(m, next);
      return json({ data: favoriteViews(ctx, p, m) });
    },

    "recents.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      const list = m ? (stateOf(p.company.id).recents.get(m) ?? []) : [];
      const data: RecentView[] = list.flatMap((r) => {
        const x = resolve(ctx, p, r.kind, r.object_id);
        return x
          ? [
              {
                kind: r.kind,
                id: r.object_id,
                title: x.title,
                path: x.path,
                space_id: x.space_id,
                board_id: x.board_id,
                viewed_at: r.viewed_at,
              },
            ]
          : [];
      });
      return json({ data });
    },
    "recents.record": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      if (!m) return forbidden("Recents are a person's own.");
      const b = (ctx.body ?? {}) as { kind?: unknown; object_id?: unknown };
      const kind = b.kind as RecentKind;
      if (!RECENT_KINDS.includes(kind)) return validation("kind: space, board, page or task");
      if (typeof b.object_id !== "string" || !UUID.test(b.object_id))
        return validation("object_id: an id");
      if (!resolve(ctx, p, kind, b.object_id)) return notFound();
      const s = stateOf(p.company.id);
      const id = b.object_id;
      const rest = (s.recents.get(m) ?? []).filter((r) => !(r.kind === kind && r.object_id === id));
      s.recents.set(
        m,
        [{ kind, object_id: id, viewed_at: stamp() }, ...rest].slice(0, RECENTS_CAP),
      );
      return noContent();
    },
  };
}
